import { ROOF_LAYOUT_FIELDS, type Job, type RoofLayout } from "@roof-hawk/core";
import { useState } from "react";

import { Stub } from "../../components/ui";
import { t } from "../../i18n";
import { fmtDateTime } from "../../lib/format";
import { MeasureRoofModal } from "./MeasureRoofModal";
import {
  RoofMeasurementModal,
  type RoofLayoutInput,
} from "./RoofMeasurementModal";

/**
 * The Roof Measurements card.
 *
 * The measuring itself already had a home — the "Roof Measurement" step on the
 * prospect checklist opens it — but a rep looking for the numbers later had no
 * reason to think of a checklist item, and this card was a stub saying the
 * feature was coming. It is the same modal, reachable from where people look.
 *
 * Two ways in, unchanged: drop the measuring service's report on it and let the
 * numbers be read off, or key them by hand.
 *
 * A third way starts here rather than finishing here — **Measure the roof**
 * hands the address over to iRoofing, to draw it, or to GAF QuickMeasure, to
 * order it (ADR 0091, ADR 0109). Either way it comes back as a report onto this
 * same card. The same choice is on the Draw Roof step of the checklist.
 */
export function RoofMeasurementsCard({
  job,
  layouts,
  onSave,
  onDelete,
  onQuickMeasureOrdered,
}: {
  job: Job;
  layouts: readonly RoofLayout[];
  onSave: (input: RoofLayoutInput) => Promise<void>;
  onDelete: (layout: RoofLayout) => Promise<void>;
  /** The rep was sent to QuickMeasure — recorded, because a credit rests on it. */
  onQuickMeasureOrdered?: () => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [measure, setMeasure] = useState(false);

  return (
    <div className="block">
      <h2>{t("Roof Measurements")}</h2>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <button className="btn-primary" onClick={() => setOpen(true)}>
          {layouts.length === 0
            ? t("Add measurements")
            : t("Add or edit measurements")}
        </button>
        {/* Getting it measured, before there is anything to add. */}
        <button className="btn-ghost" onClick={() => setMeasure(true)}>
          {t("Measure the roof")}
        </button>
      </div>

      {layouts.length === 0 ? (
        <Stub
          title={t("No measurements yet")}
          message={t(
            "Upload the measurement report and the numbers are read off it for you to confirm, or key them in by hand. Nothing measured yet? Measure the roof takes the address across to iRoofing to draw, or to QuickMeasure to order. A job can hold more than one — the house and the detached garage measure separately.",
          )}
        />
      ) : (
        <div className="ewrap" style={{ marginTop: 14 }}>
          <table className="etable">
            <thead>
              <tr>
                <th>{t("Layout")}</th>
                <th>{t("Pitch")}</th>
                <th className="num">{t("Squares")}</th>
                <th className="num">{t("Waste")}</th>
                <th className="num">{t("Layers")}</th>
                <th>{t("Source")}</th>
                <th>{t("Captured")}</th>
              </tr>
            </thead>
            <tbody>
              {layouts.map((layout) => (
                <tr key={layout.id}>
                  {/* One horizontal line, like every other table in the
                      app: the report's filename sits beside the layout name
                      rather than under it. */}
                  <td
                    title={[layout.name || t("Main roof"), layout.pdf_filename]
                      .filter(Boolean)
                      .join(" — ")}
                  >
                    <div className="rowline">
                      <span className="an">
                        {layout.name || t("Main roof")}
                      </span>
                      {layout.pdf_filename && (
                        <span className="na-hint">{layout.pdf_filename}</span>
                      )}
                    </div>
                  </td>
                  <td className="sub">{layout.average_pitch ?? "—"}</td>
                  <td className="num">{layout.squares ?? "—"}</td>
                  <td className="num">
                    {layout.waste_pct == null ? "—" : `${layout.waste_pct}%`}
                  </td>
                  <td className="num">{layout.layers ?? "—"}</td>
                  <td className="sub">
                    {layout.source === "pdf"
                      ? t("Read from report")
                      : t("Keyed in")}
                  </td>
                  <td className="sub">{fmtDateTime(layout.captured_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {layouts.length > 0 && (
        <div className="na-hint" style={{ marginTop: 10 }}>
          {t(
            "{n} measurements are held per layout. Open a layout above to see or change every one.",
            { n: ROOF_LAYOUT_FIELDS.length },
          )}
        </div>
      )}

      {measure && (
        <MeasureRoofModal
          job={job}
          onClose={() => setMeasure(false)}
          onHandoff={(service) => {
            if (service === "quickmeasure") void onQuickMeasureOrdered?.();
          }}
        />
      )}

      {open && (
        <RoofMeasurementModal
          job={job}
          layouts={layouts}
          onClose={() => setOpen(false)}
          onDelete={onDelete}
          onSave={async (input) => {
            await onSave(input);
            setOpen(false);
          }}
        />
      )}
    </div>
  );
}
